import { Injectable } from '@angular/core';
import { forkJoin } from 'rxjs';
import {
  map,
  tap
} from 'rxjs/operators';
import dayjs from "dayjs";

import {
  ApplicationOnCredit,
  ApplicationStatus, PromoDic
} from '@core/services/online-credit/online-credit.model';
import { OnlineCreditService } from '@core/services/online-credit/online-credit.service';
import { CalculationResult } from '../store/store.model';
import { DateUtilService } from "@core/services/utils/date-util.service";

@Injectable()

export class LoanCalculationService {

  private dksRates: any[];
  private interestRates: any[];
  private application?: ApplicationOnCredit;
  private amountForOnline: number;
  private maxPeriodOnline
  private salaryInterestRates: any[];
  private allProductRates: string[];
  private isPromotionalRate: boolean;


  get maxAmountOnline() {
    return this.amountForOnline;
  }

  get isPromotionalApprovedRate() {
    return this.isPromotionalRate;
  }

  get periodForOnline() {
    return this.maxPeriodOnline ? +this.maxPeriodOnline : undefined;
  }

  get isSalaryClient() {
    return this.onlineCreditService.customerAffiliation.salary
  }

  get isEmployeeClient() {
    return this.onlineCreditService.customerAffiliation.employee
  }

  public settings$ = forkJoin(
    this.onlineCreditService.getApplicationData(),
    this.onlineCreditService.fetchCalculationSettings(),
    this.onlineCreditService.fetchDependencies(),
    this.onlineCreditService.fetchPromoDic()
  ).pipe(
    tap(([application, settings, dependencies, promoDic]) => {
      this.dksRates = dependencies.dksRates;
      this.interestRates = this.isSalaryClient ? dependencies.salaryInterestRates : dependencies.interestRates;
      this.allProductRates = this.interestRates.map((v) => v.interestRate);
      this.application = application;
      this.amountForOnline = settings.maxAmountOnline;
      this.maxPeriodOnline = settings.maxPeriodOnline;
      this.setMaxDksRate(dependencies.dksRates);

      if (this.isApprovedApplication) this.changeSettingsForApprovedApplication(application, settings, promoDic);
      if (!this.isApprovedApplication && this.isEmployeeClient) this.changeSettingsForEmployeeClient(settings);
      if (!this.isApprovedApplication && this.isSalaryClient && !this.isEmployeeClient) this.changeSettingsForSalaryClient(settings)
    }),
    map(([application, settings]) => {
      settings = {
        ...settings,
        isSalaryClient: this.onlineCreditService.customerAffiliation.salary,
        isEmployeeClient: this.onlineCreditService.customerAffiliation.employee,
      }
      return {application, settings};
    }),
  );
  private maxDksRate = 0;
  private intermediateResult: CalculationResult = {
    amount: 0,
    period: 0,
    amountDks: 0,
    amountWithDks: 0,
    amountWithoutDks: 0,
    monthlyPayment: 0,
    interestRate: 0,
    incInterestRateWithoutInsurance: 0,
    insurance: true
  };

  constructor(
    private readonly onlineCreditService: OnlineCreditService
  ) {
  }

  get isApprovedApplication() {
    return this.application && this.application.status === ApplicationStatus.Approved;
  }

  calculateLoan(amount: number, period: number, withInsurance: boolean, periodIncreased: boolean) {
    this.intermediateResult.amount = amount;
    this.intermediateResult.period = period;
    this.intermediateResult.insurance = withInsurance;
    if (!this.isEmployeeClient) this.calculateDks(amount, period, withInsurance);
    this.calculateMonthlyPayment(withInsurance);
    if (this.isApprovedApplication && (this.checkIntermediatePaymentMoreMaxPayment() || periodIncreased)) {
      this.calculateMaxAmount(period, withInsurance);
      this.intermediateResult.amount = periodIncreased ? this.intermediateResult.maxAmount : this.intermediateResult.amount;
      if (this.checkIntermediatePaymentMoreMaxPayment()) {
        if (!this.isEmployeeClient) this.calculateDks(this.intermediateResult.amount, this.intermediateResult.period, withInsurance)
        this.calculateMonthlyPayment(withInsurance);
      }
    }
    ;
    return this.intermediateResult;
  }

  private setMaxDksRate(dksRates) {
    if (dksRates.length) {
      this.maxDksRate = Math.max(...dksRates.map(item => +item.toAmount));
    }
  }

  private calculateDks(amount: number, period: number, withInsurance: boolean) {
    if (amount) {
      const fixedAmount = +(amount.toFixed(2));
      let currentDksRate = this.currentDksRate(fixedAmount);

      let dksPercent = this.calculateDksPercent(period, currentDksRate.interestRate);
      let amountPercent = this.calculateAmountPercent(dksPercent);
      let amountWithDks = this.calculateAmountWithDks(amountPercent, fixedAmount);
      if (withInsurance) {
        currentDksRate = this.currentDksRateByInsurance(amountWithDks);
        dksPercent = this.calculateDksPercent(period, currentDksRate.interestRate);
        amountPercent = this.calculateAmountPercent(dksPercent);
        amountWithDks = this.calculateAmountWithDks(amountPercent, fixedAmount);
      }
      let amountDks = amountWithDks / 100 * period / 12 * +currentDksRate.interestRate;
      let amountWithoutDks = +(((fixedAmount * amountPercent) / 100).toFixed(2));

      this.intermediateResult.amountDks = amountDks;
      this.intermediateResult.amountWithDks = amountWithDks;
      this.intermediateResult.amountWithoutDks = amountWithoutDks;
    }
  }

  private currentDksRate(amount: number) {
    return this.dksRates.find((rate) => {
      const fromAmount = +rate.fromAmount;
      const toAmount = +rate.toAmount;
      return amount >= fromAmount && amount <= toAmount;
    });
  }

  private currentDksRateByInsurance(amountWithDks) {
    const maxAmount = this.maxDksRate;
    return this.dksRates.find((rate) => {
      const fromAmount = +rate.fromAmount;
      const toAmount = +rate.toAmount;
      return amountWithDks >= fromAmount && amountWithDks <= toAmount
        || (amountWithDks > maxAmount && toAmount === maxAmount);
    });
  }

  private calculateDksPercent(period, interestRate) {
    return period / 12 * +interestRate;
  }

  private calculateAmountWithDks(amountPercent, amount) {
    return +((amount / amountPercent * 100).toFixed(2));
  }

  private calculateAmountPercent(dksPercent) {
    return 100 - dksPercent;
  }

  private calculateMonthlyPayment(withInsurance: boolean) {
    const amountForCalculation = this.getAmountForCalculationMonthlyPayment(withInsurance);
    const foundedInterestRate = this.findInterestRate(amountForCalculation, withInsurance);
    const period = this.intermediateResult.period;

    this.intermediateResult.interestRate = +foundedInterestRate.interestRate;
    this.intermediateResult.incInterestRateWithoutInsurance = foundedInterestRate.incInterestRateWithoutInsurance;

    const interestRateMonth = this.intermediateResult.interestRate / 1200;

    this.intermediateResult.monthlyPayment = amountForCalculation * (interestRateMonth / (1 - Math.pow(1 + interestRateMonth, -period)));

  }

  private getAmountForCalculationMonthlyPayment(withInsurance: boolean) {
    let amountForCalculation = this.intermediateResult.amount;
    if (withInsurance) {
      amountForCalculation = this.intermediateResult.amountWithDks;
    }

    return amountForCalculation;
  }

  private findInterestRate(amount: number, withInsurance: boolean) {
    const maxDksRate = this.maxDksRate;
    const defaultInterestRate = this.interestRates.find((interestRate) => {
      const fromAmount = +interestRate.fromAmount;
      const toAmount = +interestRate.toAmount;
      return fromAmount <= +amount && toAmount >= +amount || maxDksRate === toAmount;
    });
    const approvedInterestRate = this.interestRates.find((rate) => {
      const fromAmount = +rate.fromAmount;
      const toAmount = +rate.toAmount;
      return fromAmount === +amount && toAmount === +amount && rate.forApprovedAmount;
    })
    console.log(this.interestRates);
    const productInterestRate = approvedInterestRate ? approvedInterestRate : defaultInterestRate;

    let interestRate = +productInterestRate.interestRate;
    const incInterestRate = +productInterestRate.incInterestRateWithoutInsurance;
    if (!withInsurance) {
      interestRate += incInterestRate;
    }
    return {
      incInterestRateWithoutInsurance: incInterestRate,
      interestRate: interestRate.toFixed(4)
    };
  }

  private changeSettingsForApprovedApplication(application: ApplicationOnCredit, settings, promoDic: PromoDic) {
    const approvedRateToString = application.approveRate.toFixed(2);

    if (application.approvePeriod) {
      settings.maxPeriod = application.approvePeriod;
    }
    settings.maxAmount = application.approveAmount;
    if (application.approveInsuranceAmount) {
      settings.maxAmount -= application.approveInsuranceAmount;
      settings.insurance = true;
    } else {
      settings.insurance = false;
    }
    settings.amount = settings.maxAmount;
    settings.period = settings.maxPeriod;

    if (this.approvedRateIsPromotion(approvedRateToString, promoDic)) {
      settings.minAmount = settings.minPromotionAmount;
      this.addPromotionalRate(application, settings);
      // settings.maxAmount = settings.maxPromotionAmount;
    }
    if (this.isEmployeeClient) {
      settings.maxAmountOnline = settings.maxEmployeeAmount;
      settings.minPeriod = settings.minEmployeePeriod;
      this.amountForOnline = settings.maxAmountOnline;
      if (!this.approvedRateIsPromotion(approvedRateToString, promoDic)) {
        this.addEmployeeInterestRate(settings);
        settings.minAmount = settings.minEmployeeAmount;
      }
    }
    if (this.isSalaryClient && !this.isEmployeeClient) {
      settings.maxAmountOnline = settings.maxAmountSalaryOnline;
      this.amountForOnline = settings.maxAmountOnline;
      if (!this.approvedRateIsPromotion(approvedRateToString, promoDic)) {
        settings.minAmount = settings.minSalaryAmount;
      }
    }

    // if (!this.allProductRates.includes(approvedRateToString) && !this.approvedRateIsPromotion(approvedRateToString, promoDic)) {
    //   this.addApprovedRate(application, settings)
    // }
  }

  private approvedRateIsPromotion(approvedRate: string, promoDic: PromoDic) {
    if (!promoDic) {
      return false;
    }
    const beginDate = dayjs(DateUtilService.dateYYYYMMDD(promoDic.beginDate));
    const endDate = dayjs(DateUtilService.dateYYYYMMDD(promoDic.endDate))

    this.isPromotionalRate = dayjs().isBetween(beginDate, endDate) && !this.allProductRates.includes(approvedRate);

    return this.isPromotionalRate;
  }

  private addPromotionalRate(application: ApplicationOnCredit, settings) {
    this.interestRates = [
      {
        fromAmount: settings.minPromotionAmount.toFixed(2),
        interestRate: application.approveRate.toFixed(4),
        incInterestRateWithoutInsurance: '0.00',
        toAmount: application.approveAmount.toFixed(2),
        forApprovedAmount: true
      }
    ];
  }

  private addApprovedRate(application: ApplicationOnCredit, settings) {
    this.interestRates.push({
      fromAmount: application.approveAmount.toFixed(2),
      interestRate: application.approveRate.toFixed(4),
      incInterestRateWithoutInsurance: '0.00',
      toAmount: application.approveAmount.toFixed(2),
      forApprovedAmount: true
    })
  }

  private changeSettingsForEmployeeClient(settings) {
    settings.maxAmount = settings.maxEmployeeAmount;
    settings.minAmount = settings.minEmployeeAmount;
    settings.minPeriod = settings.minEmployeePeriod;
    settings.maxPeriod = settings.maxEmployeePeriod;
    settings.maxAmountOnline = settings.maxEmployeeAmount;
    this.amountForOnline = settings.maxAmountOnline;
    settings.insurance = false
    settings.amount = settings.minAmount;
    settings.period = settings.minPeriod;
    this.addEmployeeInterestRate(settings);
  }

  private changeSettingsForSalaryClient(settings: any) {
    settings.minAmount = settings.minSalaryAmount;
    settings.maxAmount = settings.maxSalaryAmount;
    settings.maxAmountOnline = settings.maxAmountSalaryOnline;
    this.amountForOnline = settings.maxAmountOnline;
  }

  private addEmployeeInterestRate(settings) {
    this.interestRates = [
      {
        fromAmount: settings.minAmount.toFixed(2),
        interestRate: settings.interestRateEmployee.toFixed(4),
        incInterestRateWithoutInsurance: '0.00',
        toAmount: settings.maxAmount.toFixed(2)
      }
    ]
  }

  private checkIntermediatePaymentMoreMaxPayment() {
    return this.isApprovedApplication && this.intermediateResult.monthlyPayment > this.application.approveMonthAmountMax;
  }

  private calculateMaxAmount(period: number, withInsurance: boolean) {
    const interestRateMonth = this.intermediateResult.interestRate / 1200;
    const approvedMonthAmountMax = this.application.approveMonthAmountMax;
    let approvedMaxAmount = this.application.approveAmount;
    let amount = approvedMonthAmountMax / (interestRateMonth / (1 - Math.pow(1 + interestRateMonth, -period)));
    if (withInsurance) {
      approvedMaxAmount -= +this.application.approveInsuranceAmount;
      const currentDksRate = this.currentDksRateByInsurance(amount);
      const dksAmount = amount / 100 * period / 12 * +currentDksRate.interestRate;
      amount = amount - dksAmount;
    }
    if (amount > approvedMaxAmount) {
      amount = approvedMaxAmount;
    }
    if (amount > this.amountForOnline) {
      amount = this.amountForOnline;
    }
    this.intermediateResult.maxAmount = amount;
  }

}
